import { useRef, useCallback } from 'react';

interface UseSwipeToDeleteOptions {
  onDelete: () => void;
  threshold?: number; // Minimum swipe distance to trigger delete (default: 100px)
  enabled?: boolean; // Whether swipe is enabled (default: true)
}

interface SwipeHandlers {
  onTouchStart: (e: React.TouchEvent) => void;
  onTouchMove: (e: React.TouchEvent) => void;
  onTouchEnd: (e: React.TouchEvent) => void;
}

export function useSwipeToDelete({
  onDelete,
  threshold = 100,
  enabled = true
}: UseSwipeToDeleteOptions): SwipeHandlers {
  const startXRef = useRef<number>(0);
  const currentXRef = useRef<number>(0);
  const isDraggingRef = useRef<boolean>(false);
  const elementRef = useRef<HTMLElement | null>(null);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    if (!enabled) return;

    const touch = e.touches[0];
    startXRef.current = touch.clientX;
    currentXRef.current = touch.clientX;
    isDraggingRef.current = true;
    elementRef.current = e.currentTarget as HTMLElement;

    // Add transition class for smooth animation
    if (elementRef.current) {
      elementRef.current.style.transition = 'none';
    }
  }, [enabled]);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    if (!enabled || !isDraggingRef.current || !elementRef.current) return;

    const touch = e.touches[0];
    currentXRef.current = touch.clientX;
    const deltaX = currentXRef.current - startXRef.current;

    // Only allow left swipe (negative delta)
    if (deltaX < 0) {
      const absDeltaX = Math.abs(deltaX);

      // Apply transform with resistance effect
      const resistance = 0.7;
      const translateX = -absDeltaX * resistance;

      elementRef.current.style.transform = `translateX(${translateX}px)`;

      // Show delete indicator color with opacity based on swipe distance
      const opacity = Math.min(1, absDeltaX / threshold);
      elementRef.current.style.backgroundColor = `rgba(239, 68, 68, ${opacity * 0.2})`;
    }
  }, [enabled, threshold]);

  const onTouchEnd = useCallback((e: React.TouchEvent) => {
    if (!enabled || !isDraggingRef.current || !elementRef.current) return;

    const deltaX = currentXRef.current - startXRef.current;
    const absDeltaX = Math.abs(deltaX);

    // Add transition for smooth snap back or delete animation
    elementRef.current.style.transition = 'all 0.3s ease-out';

    // Check if swipe threshold was met
    if (deltaX < 0 && absDeltaX >= threshold) {
      // Animate out before deleting
      elementRef.current.style.transform = 'translateX(-100%)';
      elementRef.current.style.opacity = '0';

      // Call onDelete after animation completes
      setTimeout(() => {
        onDelete();
      }, 300);
    } else {
      // Snap back to original position
      elementRef.current.style.transform = '';
      elementRef.current.style.backgroundColor = '';
    }

    // Reset state
    isDraggingRef.current = false;
    startXRef.current = 0;
    currentXRef.current = 0;
  }, [enabled, threshold, onDelete]);

  return {
    onTouchStart,
    onTouchMove,
    onTouchEnd
  };
}
